import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, UploadCloud, FileCheck2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API, { formatApiError } from "@/lib/api";
import { isInAppBrowser } from "@/lib/inAppBrowser";
import { startPayment } from "@/lib/razorpay";

const MORNING_SLOTS = ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM"];
const EVENING_SLOTS = ["6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM"];

export default function ConsultancyModal({ service, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", dob: "", birth_time: "", birth_place: "" });
  const [docUrl, setDocUrl] = useState("");
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [period, setPeriod] = useState("morning");
  const [slot, setSlot] = useState("");
  const [errors, setErrors] = useState({});
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (service) {
      setForm({ name: "", phone: "", email: "", dob: "", birth_time: "", birth_place: "" });
      setDocUrl("");
      setDocName("");
      setPeriod("morning");
      setSlot("");
      setErrors({});
      setSuccess(false);
    }
  }, [service]);

  if (!service) return null;
  const isAstro = service.id === "astrology";
  const docLabel = service.id === "graphology"
    ? "Upload your handwriting - 2 pages, ~200 legible words (PDF or JPG)"
    : "Upload your signature (PDF or JPG)";

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/uploads", fd);
      setDocUrl(`${window.location.origin}${data.url}`);
      setDocName(file.name);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your full name";
    if (!/^[0-9]{10}$/.test(form.phone.trim())) errs.phone = "Enter a valid 10-digit phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (isAstro) {
      if (!form.dob) errs.dob = "Select your date of birth";
      if (!form.birth_time) errs.birth_time = "Select your time of birth";
      if (form.birth_place.trim().length < 2) errs.birth_place = "Enter your place of birth";
    } else if (!docUrl) {
      errs.document = "Please upload the required document";
    }
    if (!slot) errs.slot = "Please choose a time slot";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleVerify = async (orderData, razorpayResponse) => {
    try {
      await API.post("/consultancy/verify", {
        booking_id: orderData.booking_id,
        razorpay_order_id: razorpayResponse.razorpay_order_id,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
      });
      setSuccess(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setPaying(true);
    try {
      const { data: orderData } = await API.post("/consultancy/create-order", {
        service_id: service.id,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        slot_period: period,
        slot_time: slot,
        dob: form.dob || null,
        birth_time: form.birth_time || null,
        birth_place: form.birth_place.trim() || null,
        document_url: docUrl || null,
      });
      await startPayment({
        order: orderData,
        description: orderData.service_name,
        prefill: { name: form.name.trim(), email: form.email.trim(), contact: form.phone.trim() },
        callbackPath: "/api/consultancy/razorpay-callback",
        cancelPath: "/payment-status?type=consultancy&status=failed",
        onSuccess: (resp) => handleVerify(orderData, resp),
        onDismiss: () => setPaying(false),
        onFailure: (resp) => {
          API.post("/consultancy/failed", {
            booking_id: orderData.booking_id,
            reason: resp?.error?.description || "Payment failed",
          }).catch(() => {});
          setPaying(false);
          toast.error("Payment failed. Please try again.");
        },
      });
    } catch (e2) {
      setPaying(false);
      const msg = formatApiError(e2);
      if (msg === "payments_not_configured") {
        toast.error("Online payments are being set up. Please try again shortly.");
      } else {
        toast.error(msg);
      }
    }
  };

  const slots = period === "morning" ? MORNING_SLOTS : EVENING_SLOTS;

  return (
    <Dialog open={!!service} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md bg-[#FDFBF7] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="consultancy-modal">
        {success ? (
          <div className="text-center py-6" data-testid="consultancy-success">
            <CheckCircle2 className="mx-auto text-[#2D5A47]" size={52} />
            <h3 className="font-display text-2xl font-bold text-[#2D5A47] mt-4">Booking Confirmed!</h3>
            <p className="text-base text-[#1E2A24] font-semibold mt-3" data-testid="consultancy-success-message">
              Within 48 hours our team will connect with you
            </p>
            <p className="text-sm text-[#5A6E63] mt-3 leading-relaxed">
              {service.name} &middot; {slot} ({period === "morning" ? "Morning" : "Evening"} slot).
              Keep your phone handy - we will reach you on {form.phone}.
            </p>
            <Button onClick={onClose} data-testid="consultancy-success-close"
              className="mt-6 rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7] px-8">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[#2D5A47]">{service.name}</DialogTitle>
              <DialogDescription className="text-[#5A6E63]">
                One-on-one video consultation &middot;{" "}
                <span className="font-semibold text-[#1E2A24]">&#8377;{service.price}</span>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="consultancy-form">
              <div>
                <Label htmlFor="c-input-name" className="text-xs text-[#5A6E63]">Name</Label>
                <Input id="c-input-name" data-testid="c-input-name" value={form.name} onChange={set("name")}
                  placeholder="Your full name" className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
                {errors.name && <p data-testid="c-error-name" className="text-xs text-red-600 mt-1">{errors.name}</p>}
              </div>
              {isAstro && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="c-input-dob" className="text-xs text-[#5A6E63]">Date of Birth</Label>
                      <Input id="c-input-dob" data-testid="c-input-dob" type="date" value={form.dob} onChange={set("dob")}
                        className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
                      {errors.dob && <p data-testid="c-error-dob" className="text-xs text-red-600 mt-1">{errors.dob}</p>}
                    </div>
                    <div>
                      <Label htmlFor="c-input-birth-time" className="text-xs text-[#5A6E63]">Time of Birth</Label>
                      <Input id="c-input-birth-time" data-testid="c-input-birth-time" type="time" value={form.birth_time}
                        onChange={set("birth_time")} className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
                      {errors.birth_time && <p data-testid="c-error-birth-time" className="text-xs text-red-600 mt-1">{errors.birth_time}</p>}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-input-birth-place" className="text-xs text-[#5A6E63]">Place of Birth</Label>
                    <Input id="c-input-birth-place" data-testid="c-input-birth-place" value={form.birth_place}
                      onChange={set("birth_place")} placeholder="e.g. Guwahati, Assam"
                      className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
                    {errors.birth_place && <p data-testid="c-error-birth-place" className="text-xs text-red-600 mt-1">{errors.birth_place}</p>}
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="c-input-phone" className="text-xs text-[#5A6E63]">Phone number</Label>
                <Input id="c-input-phone" data-testid="c-input-phone" type="tel" value={form.phone} onChange={set("phone")}
                  placeholder="10-digit phone number" className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
                {errors.phone && <p data-testid="c-error-phone" className="text-xs text-red-600 mt-1">{errors.phone}</p>}
              </div>
              <div>
                <Label htmlFor="c-input-email" className="text-xs text-[#5A6E63]">Email Id</Label>
                <Input id="c-input-email" data-testid="c-input-email" type="email" value={form.email} onChange={set("email")}
                  placeholder="you@example.com" className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
                {errors.email && <p data-testid="c-error-email" className="text-xs text-red-600 mt-1">{errors.email}</p>}
              </div>
              {!isAstro && (
                <div>
                  <Label className="text-xs text-[#5A6E63]">{docLabel}</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <Button type="button" variant="outline" size="sm" data-testid="c-file-upload"
                      disabled={uploading}
                      onClick={() => document.getElementById("c-file-input")?.click()}
                      className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE]">
                      <UploadCloud size={14} className="mr-1.5" /> {uploading ? "Uploading..." : "Choose File"}
                    </Button>
                    <input id="c-file-input" data-testid="c-file-input" type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                      onChange={handleDocUpload} />
                    {docName && (
                      <span className="flex items-center gap-1 text-xs text-[#2D5A47] font-medium" data-testid="c-file-uploaded">
                        <FileCheck2 size={13} /> {docName}
                      </span>
                    )}
                  </div>
                  {errors.document && <p data-testid="c-error-document" className="text-xs text-red-600 mt-1">{errors.document}</p>}
                </div>
              )}
              <div>
                <Label className="text-xs text-[#5A6E63]">Choose your time slot</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <button type="button" data-testid="slot-period-morning" onClick={() => { setPeriod("morning"); setSlot(""); }}
                    className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${period === "morning" ? "bg-[#2D5A47] text-[#FDFBF7] border-[#2D5A47]" : "border-[#2D5A47]/25 text-[#2D5A47] hover:bg-[#E8F3EE]"}`}>
                    Morning (8 AM - 12 PM)
                  </button>
                  <button type="button" data-testid="slot-period-evening" onClick={() => { setPeriod("evening"); setSlot(""); }}
                    className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${period === "evening" ? "bg-[#2D5A47] text-[#FDFBF7] border-[#2D5A47]" : "border-[#2D5A47]/25 text-[#2D5A47] hover:bg-[#E8F3EE]"}`}>
                    Evening (6 PM - 10 PM)
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {slots.map((s) => (
                    <button key={s} type="button" data-testid={`slot-chip-${s.replace(/[: ]/g, "-")}`}
                      onClick={() => setSlot(s)}
                      className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${slot === s ? "bg-[#D98E32] text-[#FDFBF7] border-[#D98E32]" : "border-[#2D5A47]/25 text-[#5A6E63] hover:bg-[#E8F3EE]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
                {errors.slot && <p data-testid="c-error-slot" className="text-xs text-red-600 mt-1">{errors.slot}</p>}
              </div>
              {isInAppBrowser() && (
                <div data-testid="inapp-payment-warning" className="bg-[#F9EBEA] border border-[#96690F]/30 rounded-xl p-3 text-xs text-[#96690F] leading-relaxed">
                  You will be taken to Razorpay&apos;s secure payment page. Pay with <strong>UPI ID, Card or Netbanking</strong> - you will be brought back here automatically after payment.
                </div>
              )}
              <Button type="submit" data-testid="consultancy-pay-button" disabled={paying}
                className="w-full rounded-full bg-[#D98E32] hover:bg-[#c17a24] text-[#FDFBF7] h-11 mt-2">
                {paying ? (
                  <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Processing...</span>
                ) : (
                  <>Book &amp; Pay &#8377;{service.price}</>
                )}
              </Button>
              <p className="text-[11px] text-center text-[#5A6E63]">
                Secure payment via Razorpay. Within 48 hours our team will connect with you.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
